import { cn } from "@/lib/utils";

export type ConceptChipOption = {
  id: string;
  name: string;
};

/** Toggle-chip multi-select for Brand Universe concept membership. Max optional. */
export function ConceptChipPicker({
  options,
  value,
  onChange,
  max = 3,
  disabled = false,
  className,
}: {
  options: ConceptChipOption[];
  value: string[];
  onChange: (next: string[]) => void;
  max?: number;
  disabled?: boolean;
  className?: string;
}) {
  const selected = new Set(value);

  const toggle = (id: string) => {
    if (disabled) return;
    const next = new Set(selected);
    if (next.has(id)) {
      next.delete(id);
    } else {
      if (next.size >= max) return;
      next.add(id);
    }
    // Preserve option order regardless of click order.
    onChange(options.filter((o) => next.has(o.id)).map((o) => o.id));
  };

  if (options.length === 0) {
    return (
      <p className={cn("text-[11px] text-co-faint", className)}>
        No concepts available yet.
      </p>
    );
  }

  return (
    <div className={cn("flex flex-wrap gap-1.5", className)}>
      {options.map((option) => {
        const on = selected.has(option.id);
        const atCap = !on && selected.size >= max;
        return (
          <button
            key={option.id}
            type="button"
            role="switch"
            aria-checked={on}
            disabled={disabled || atCap}
            onClick={() => toggle(option.id)}
            title={atCap ? `At most ${max} concepts` : option.name}
            className={cn(
              "inline-flex items-center gap-1.5 rounded-tag border px-2 py-0.5 text-[11px] font-medium transition-colors",
              on
                ? "border-co-accent-border bg-co-accent-dim text-co-accent-ink"
                : "border-co-line bg-co-surface-solid text-co-faint hover:border-co-line2 hover:text-co-muted",
              (disabled || atCap) && !on ? "opacity-40" : null,
            )}
          >
            {option.name}
          </button>
        );
      })}
    </div>
  );
}

/** Read-only concept name chips. */
export function ConceptChipBadges({
  options,
  conceptIds,
  className,
}: {
  options: ConceptChipOption[];
  conceptIds: string[];
  className?: string;
}) {
  if (conceptIds.length === 0) return null;
  const byId = new Map(options.map((o) => [o.id, o.name]));
  const labels = conceptIds
    .map((id) => byId.get(id) ?? id.slice(0, 8))
    .filter(Boolean);
  if (labels.length === 0) return null;

  return (
    <div className={cn("mt-1.5 flex flex-wrap gap-1", className)}>
      {labels.map((label) => (
        <span
          key={label}
          className="inline-flex items-center rounded-tag bg-co-surface-solid px-1.5 py-0.5 text-[10px] font-medium text-co-muted"
        >
          {label}
        </span>
      ))}
    </div>
  );
}
